import { useEffect } from 'react'
import { useQuery, type QueryKey } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import UnderlineExtension from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import type { CommitmentLetterPreviewResponse } from '@/api/commitmentLetters'

interface LetterPreviewProps {
  queryKey: QueryKey
  queryFn: () => Promise<CommitmentLetterPreviewResponse>
  enabled: boolean
}

/** Read-only render of one vendor's filled commitment letter. */
export function LetterPreview({
  queryKey,
  queryFn,
  enabled,
}: LetterPreviewProps) {
  const preview = useQuery({ queryKey, queryFn, enabled })

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      UnderlineExtension,
      TextStyle,
    ],
    content: '',
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-0 focus:outline-none',
      },
    },
  })

  useEffect(() => {
    editor?.commands.setContent(preview.data?.content ?? '')
  }, [editor, preview.data])

  return (
    <div className="relative min-h-[240px] flex-1 overflow-y-auto rounded-md border border-[#d6dae1] bg-white p-6">
      {preview.isFetching && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {preview.isError ? (
        <p className="text-sm text-destructive">
          Failed to load the letter preview.
        </p>
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  )
}
